/**
 * PortfolioView
 * Shows all portfolio positions grouped by exchange with asset distribution chart.
 * Positions are polled live every 15 seconds via /api/portfolio/live.
 */
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { useApiClient } from '../api/client';
import type { PortfolioPosition, UserProfile, PortfolioByExchange } from '../api/client';
import { AssetDistributionChart } from '../components/dashboard/PortfolioChart';
import { useLivePortfolio } from '../hooks/useLivePortfolio';
import { parseDbDate } from '../lib/date';

const TYPE_BADGE: Record<string, string> = {
  crypto: 'badge-warning',
  equity: 'badge-info',
  etf: 'badge-success',
  cash: 'badge-neutral',
};

function exportToCsv(
  positions: PortfolioPosition[],
  exchangeName: string,
  label: string | null | undefined,
  headers: string[],
) {
  const filename = `portfolio_${exchangeName}${label ? `_${label}` : ''}_${new Date().toISOString().slice(0, 10)}.csv`;
  const rows = positions.map((p) => [
    p.assetSymbol,
    p.assetType,
    p.quantity,
    p.avgBuyPrice ?? '',
    p.lastPrice ?? '',
    p.currency,
    parseDbDate(p.updatedAt).toLocaleDateString('tr-TR'),
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatNum(n: number | undefined, decimals = 4, minDecimals = 0): string {
  if (n == null) return '—';
  return n.toLocaleString('tr-TR', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: decimals,
  });
}

export default function PortfolioView() {
  const { _ } = useLingui();
  const api = useApiClient();
  // Exchange metadata — loaded once for the tab/group structure
  const [exchangeGroups, setExchangeGroups] = useState<PortfolioByExchange[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeExchange, setActiveExchange] = useState<string | null>(null);

  // Live-priced positions — polls every 15 s
  const { liveData, loading: liveLoading, lastUpdated: _lastUpdated } = useLivePortfolio();

  useEffect(() => {
    void Promise.all([api.getPortfolioByExchange(), api.getProfile()])
      .then(([data, prof]) => {
        setExchangeGroups(data);
        setProfile(prof);
        if (data.length > 0) setActiveExchange(data[0].userExchangeId);
      })
      .catch(() => setError(_({ id: 'portfolio.error.loadFailed' })))
      .finally(() => setMetaLoading(false));
  }, [api, _]);

  // Merge live positions into groups (positions come from live, metadata from groups)
  const grouped: PortfolioByExchange[] = exchangeGroups.map((g) => {
    if (!liveData) return g;

    const livePositions = liveData.positions.filter((p) => p.userExchangeId === g.userExchangeId);
    if (livePositions.length === 0) return g;

    // Use per-exchange total from the server when available (native exchanges like T212 provide
    // their own account total which matches the dashboard summary/live calculation).
    // Fall back to summing individual position values for non-native exchanges.
    const totalValueInCurrency =
      liveData.exchangeTotals?.[g.userExchangeId] ??
      livePositions.reduce((sum, p) => sum + (p.valueInCurrency ?? 0), 0);
    const totalCostInCurrency = livePositions.reduce((sum, p) => sum + (p.costInCurrency ?? 0), 0);

    return {
      ...g,
      positions: livePositions,
      totalValueInCurrency,
      totalCostInCurrency,
    };
  });

  // Flatten all positions for the chart
  const allPositions: PortfolioPosition[] = grouped.flatMap((g) => g.positions);
  const byAssetType = allPositions.reduce<Record<string, number>>((acc, p) => {
    acc[p.assetType] = (acc[p.assetType] ?? 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(byAssetType).map(([name, value]) => ({ name, value }));

  // Totals: prefer exchange-level totals (native values) over summing individual positions
  const displayCurrency = allPositions[0]?.displayCurrency ?? profile?.preferredCurrency ?? 'USD';
  const preferredTotal =
    grouped.length > 0
      ? {
          currentValue: grouped.reduce((s, g) => s + g.totalValueInCurrency, 0),
          costBasis: grouped.reduce((s, g) => s + g.totalCostInCurrency, 0),
        }
      : null;

  const activeGroup = grouped.find((g) => g.userExchangeId === activeExchange);

  if (metaLoading || liveLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <div className="alert alert-error">{error}</div>}

      {grouped.length === 0 ? (
        <div className="card bg-base-100 shadow">
          <div className="card-body items-center text-center">
            <h2 className="card-title">{_({ id: 'portfolio.empty.title' })}</h2>
            <p className="text-base-content/60">{_({ id: 'portfolio.empty.description' })}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Toplam Değer Özeti */}
          {preferredTotal &&
            (() => {
              const pnl = preferredTotal.currentValue - preferredTotal.costBasis;
              const pnlPct =
                preferredTotal.costBasis > 0 ? (pnl / preferredTotal.costBasis) * 100 : 0;
              const isPos = pnl >= 0;
              return (
                <div className="stats stats-vertical sm:stats-horizontal shadow w-full bg-base-100 border border-base-300">
                  <div className="stat">
                    <div className="stat-title">
                      {_({
                        id: 'portfolio.totalValue.title',
                        values: { currency: displayCurrency },
                      })}
                    </div>
                    <div className="stat-value text-2xl">
                      {preferredTotal.currentValue.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div className={`stat-desc ${isPos ? 'text-success' : 'text-error'}`}>
                      {isPos ? '+' : ''}
                      {pnl.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      ({isPos ? '+' : ''}
                      {pnlPct.toFixed(2)}%)
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Asset Distribution */}
          {pieData.length > 0 && <AssetDistributionChart data={pieData} />}

          {/* Exchange Tabs */}
          <div className="overflow-x-auto">
            <div className="tabs tabs-boxed w-max min-w-full sm:w-auto">
              {grouped.map((g) => (
                <button
                  key={g.userExchangeId}
                  className={`tab flex items-center gap-1.5 whitespace-nowrap ${activeExchange === g.userExchangeId ? 'tab-active' : ''}`}
                  onClick={() => setActiveExchange(g.userExchangeId)}
                >
                  <span>{g.exchangeName}</span>
                  {g.label && <span className="text-xs opacity-60">({g.label})</span>}
                  <span className="badge badge-sm">{g.positions.length}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Positions Table */}
          {activeGroup && (
            <div className="card bg-base-100 shadow border border-base-300">
              <div className="flex justify-end px-4 pt-3">
                <button
                  className="btn btn-sm btn-outline gap-1.5"
                  onClick={() =>
                    exportToCsv(
                      activeGroup.positions,
                      activeGroup.exchangeName,
                      activeGroup.label,
                      [
                        _({ id: 'portfolio.table.colSymbol' }),
                        _({ id: 'portfolio.table.colType' }),
                        _({ id: 'portfolio.table.colQuantity' }),
                        _({ id: 'portfolio.table.colAvgCost' }),
                        _({ id: 'portfolio.table.colPrice' }),
                        _({ id: 'portfolio.table.colCurrency' }),
                        _({ id: 'portfolio.table.colUpdated' }),
                      ],
                    )
                  }
                  disabled={activeGroup.positions.length === 0}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  CSV İndir
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="table table-zebra min-w-215">
                  <thead>
                    <tr>
                      <th>{_({ id: 'portfolio.table.colSymbol' })}</th>
                      <th>{_({ id: 'portfolio.table.colType' })}</th>
                      <th>{_({ id: 'portfolio.table.colQuantity' })}</th>
                      <th>{_({ id: 'portfolio.table.colAvgCost' })}</th>
                      <th>{_({ id: 'portfolio.table.colPrice' })}</th>
                      <th>{_({ id: 'portfolio.table.colCurrency' })}</th>
                      <th>{_({ id: 'portfolio.table.colUpdated' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeGroup.positions.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-base-content/50 py-8">
                          {_({ id: 'portfolio.table.empty' })}
                        </td>
                      </tr>
                    )}
                    {activeGroup.positions.map((pos) => (
                      <tr key={pos.id ?? pos.assetSymbol}>
                        <td>
                          <span className="font-mono font-bold">{pos.assetSymbol}</span>
                        </td>
                        <td>
                          <span
                            className={`badge badge-sm ${TYPE_BADGE[pos.assetType] ?? 'badge-neutral'}`}
                          >
                            {pos.assetType}
                          </span>
                        </td>
                        <td className="font-mono">{formatNum(pos.quantity)}</td>
                        <td className="font-mono">{formatNum(pos.avgBuyPrice, 2, 2)}</td>
                        <td className="font-mono">{formatNum(pos.lastPrice, 2, 2)}</td>
                        <td>
                          <span className="badge badge-ghost badge-sm">{pos.currency}</span>
                        </td>
                        <td className="text-xs text-base-content/50">
                          {parseDbDate(pos.updatedAt).toLocaleDateString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
